import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { grouped } from "@/entities/scene-card/model/creditRates";

/**
 * 组图单张重出面板(B2-3,2026-07-31)——「改这条爆点、只重出这一张」。
 *
 * ═══ 为什么这条是硬指标 ═══════════════════════════════════════════════════
 * 2026-07-31 用户定:**不做标题文字层,大字交给模型画,糊了再出一张**。
 * 「再出一张」要成立,这条路就必须 ①便宜(单张,不是整组)②快 ③**不破坏整组一致性**。
 * 所以它锚的是封面,不是这张自己(锚自己会把画面上那行旧大字一起保留下来 —— 而要改的
 * 正是它)。
 *
 * ═══ 界面上的两条纪律 ═════════════════════════════════════════════════════
 * ① 画面提示**不填就不动它**:用户只想改标题时,他之前写的画面提示不能跟着消失
 *    (后端 detail=null 保持原样、""=主动清空,这里只在打开输入口时才传)。
 * ② 封面重出要**说出代价**:其余几张是照着旧封面画的,换了封面它们就失去参照。
 *    这是可见的代价,不能默默发生。
 */
export function GroupRedrawPanel({
  seq,
  isCover,
  initialTitle,
  otherCount,
  credits,
  busy,
  onRedraw,
}: {
  seq: number;
  isCover: boolean;
  initialTitle: string;
  /** 除这张外还有几张(封面重出时用来说清代价)。 */
  otherCount: number;
  /** 每张积分;价目表没到货时为 null —— 按钮上不出数字,不出一个可能过期的数。 */
  credits: number | null;
  busy?: boolean;
  onRedraw: (title: string, detail?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(initialTitle);
  const [detailOn, setDetailOn] = useState(false);
  const [detail, setDetail] = useState("");

  // 切换选中的那一张 → 表单跟着换(否则第 2 张的输入框里留着第 1 张的字)。
  // ★B-02:只认 seq,不认 initialTitle —— initialTitle 来自 5s 轮询的 task,
  //   把它放进依赖,用户打字打到一半轮询一回来整块被重置(光标被拽走、面板收起)。
  useEffect(() => {
    setTitle(initialTitle);
    setDetail("");
    setDetailOn(false);
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seq]);
  // 面板没打开时输入框根本没渲染,跟着轮询把迟到的标题同步进来是安全的;
  // 打开后就不再动用户手里的值。
  useEffect(() => {
    if (!open) setTitle(initialTitle);
  }, [open, initialTitle]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
      >
        这张的字不对?{" "}
        <span className="font-semibold text-primary underline-offset-4 hover:underline">
          改文案重出这一张 ▸
        </span>
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-dashed border-border/70 bg-muted/20 p-2.5">
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-[12px] text-muted-foreground">大字</span>
        <Input
          value={title}
          disabled={busy}
          // B-02:不在受控值上二次归一(上限交给 maxLength),否则顶到上限光标被拽到末尾
          onChange={(e) => setTitle(e.target.value)}
          aria-label={`第 ${seq} 张的大字`}
          className="h-7 flex-1 text-[13px]"
          maxLength={30}
        />
      </div>

      {detailOn ? (
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-[12px] text-muted-foreground">画面</span>
          <Input
            value={detail}
            disabled={busy}
            onChange={(e) => setDetail(e.target.value)}
            aria-label={`第 ${seq} 张的画面提示`}
            placeholder="这张画什么(留空 = 清掉原来的)"
            className="h-7 flex-1 text-[12.5px]"
            maxLength={120}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setDetailOn(true)}
          className="text-[12px] text-muted-foreground underline-offset-2 hover:underline"
        >
          也改画面提示 ▸（不动它就别打开）
        </button>
      )}

      {/* ★封面重出的代价必须说出来 */}
      {isCover && otherCount > 0 && (
        <p className="text-[12px] leading-relaxed text-warning">
          这是封面,也是整组的参照。换掉它之后,已经出好的另外 {otherCount} 张仍然是照旧封面画的 ——
          想整组统一就得把它们也重出一遍。
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={busy || !title.trim()}
          onClick={() => onRedraw(title.trim(), detailOn ? detail : undefined)}
          className="h-8 gap-1.5 px-3 text-[12.5px] font-semibold"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          重出这一张
          {credits == null ? "" : ` · ${grouped(credits)} 积分`}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => setOpen(false)}
          className="h-8 px-2 text-[12.5px]"
        >
          取消
        </Button>
        {!isCover && <span className="text-[12px] text-muted-foreground">会照着封面的版式出</span>}
      </div>
    </div>
  );
}
